import { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import {
  Box,
  Checkbox,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { OnChangeValue } from 'react-select';
import { InviteUserDropdownOption } from '../../DropdownSelect';
import { UserChips } from '../components/UserChips';

interface Props {
  availableTeamMembers: InviteUserDropdownOption[];
  selectedTeamMembers: InviteUserDropdownOption[];
  handleSelectTeamMembers: (
    optionsSelected: OnChangeValue<InviteUserDropdownOption, boolean>,
  ) => void;
  selectedSuperAdmins: InviteUserDropdownOption[];
  selectedApprovers: InviteUserDropdownOption[];
  handleDeleteUserChip: (user: InviteUserDropdownOption) => void;
}

export const PeopleStep = ({
  availableTeamMembers,
  selectedTeamMembers,
  handleSelectTeamMembers,
  selectedSuperAdmins,
  selectedApprovers,
  handleDeleteUserChip,
}: Props) => {
  const { tokens } = useTheme<LensTheme>();
  const sp = tokens.semantic.spacing.desktop.gap;
  const [searchQuery, setSearchQuery] = useState('');

  const filteredMembers = availableTeamMembers.filter((member) => {
    const query = searchQuery.toLowerCase();
    return (
      member.label.toLowerCase().includes(query) ||
      member.role?.toLowerCase().includes(query) ||
      member.site?.toLowerCase().includes(query)
    );
  });

  const handleCheckboxChange = (row: InviteUserDropdownOption) => {
    const isSelected = selectedTeamMembers.some((m) => m.value === row.value);
    const updated = isSelected
      ? selectedTeamMembers.filter((m) => m.value !== row.value)
      : [...selectedTeamMembers, row];
    handleSelectTeamMembers(updated);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: sp[3].value }}>
      <Box>
        <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
          Invite people to your disclosure
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Expand your team by inviting individuals to collaborate on your case.
        </Typography>
      </Box>
      <Box>
        <TextField
          label="Search"
          sx={{ display: 'flex' }}
          size="medium"
          value={searchQuery}
          placeholder="Search someone from employee directory"
          onChange={(e) => setSearchQuery(e.target.value)}
          inputProps={{ 'data-testid': 'people-search' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <span className="uil uil-search" />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Box>
        <TableContainer sx={{ maxHeight: '450px', overflowY: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>Employee name</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Department</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredMembers.map((row) => (
                <TableRow key={row.value} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={
                        row.teamMember ||
                        selectedTeamMembers.some((m) => m.value === row.value)
                      }
                      disabled={row.teamMember}
                      onChange={() => handleCheckboxChange(row)}
                      aria-label={`Select ${row.label}`}
                    />
                  </TableCell>
                  <TableCell>{row.label}</TableCell>
                  <TableCell>{row.role}</TableCell>
                  <TableCell>{row.site}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <UserChips
        selectedSuperAdmins={selectedSuperAdmins}
        selectedApprovers={selectedApprovers}
        handleDeleteUserChip={handleDeleteUserChip}
      />
    </Box>
  );
};
